import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShoppingCart, Clock, DollarSign, ArrowUpRight } from 'lucide-react';

const REVENUE_IMAGE = '/assets/revenue-section.jpg';

const RevenueSection = () => {
  const [counters, setCounters] = useState({ conversion: 0, recovery: 0, support: 0 });

  useEffect(() => {
    const duration = 2000;
    const targets = { conversion: 30, recovery: 45, support: 60 };
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      setCounters({
        conversion: Math.round((targets.conversion / steps) * step),
        recovery: Math.round((targets.recovery / steps) * step),
        support: Math.round((targets.support / steps) * step)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      icon: TrendingUp,
      value: `+${counters.conversion}%`,
      label: 'Conversion Rate',
      subtext: 'AI-driven increase',
      color: 'bg-teal-50 border-teal-200 text-teal-600'
    },
    {
      icon: ShoppingCart,
      value: `${counters.recovery}%`,
      label: 'Cart Recovery',
      subtext: 'Automated saves',
      color: 'bg-cyan-50 border-cyan-200 text-cyan-600'
    },
    {
      icon: Clock,
      value: '24/7',
      label: 'AI Availability',
      subtext: 'Never offline',
      color: 'bg-emerald-50 border-emerald-200 text-emerald-600'
    },
    {
      icon: DollarSign,
      value: `${counters.support}%`,
      label: 'Support Savings',
      subtext: 'Cost reduction',
      color: 'bg-green-50 border-green-200 text-green-600'
    }
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-white" data-testid="revenue-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <ArrowUpRight className="w-4 h-4 text-emerald-600" />
            <span className="text-xs sm:text-sm font-bold text-emerald-700">Revenue Impact</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-5">
            AI-Driven Results,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Not Just Replies</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Every AI feature is engineered for one goal: turning traffic into revenue.
          </p>
        </motion.div>

        {/* Two-column: Stats + Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`stat-card-${index}`}
              >
                <div className={`bg-white border-2 ${stat.color} rounded-2xl sm:rounded-3xl p-4 sm:p-6 h-full shadow-sm hover:shadow-lg transition-all duration-300`}>
                  <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-xl sm:rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 sm:mb-4">
                    <stat.icon className="w-5 sm:w-6 h-5 sm:h-6" />
                  </div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-1 sm:mb-2 font-mono tracking-tight">{stat.value}</p>
                  <p className="text-sm sm:text-base font-bold text-slate-800 mb-0.5">{stat.label}</p>
                  <p className="text-xs sm:text-sm text-slate-500">{stat.subtext}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Image Side */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={REVENUE_IMAGE}
              alt="Business analytics showing growth from AI-powered e-commerce"
              className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[280px] sm:h-[380px] lg:h-[420px] object-cover border-2 border-slate-100"
              data-testid="revenue-image"
            />
            <motion.div 
              className="absolute -top-4 -left-3 sm:-left-5 bg-emerald-600 text-white px-4 sm:px-5 py-2 sm:py-3 rounded-xl shadow-lg"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 2.5 }}
            >
              <p className="text-xs sm:text-sm font-bold">Revenue Growth</p>
              <p className="text-lg sm:text-xl font-extrabold">+30% avg</p>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="mt-12 sm:mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-slate-600 text-sm sm:text-lg mb-4 sm:mb-5">Empower your e-commerce store with Arismate AI</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {['Fashion', 'Electronics', 'Beauty', 'D2C'].map((category) => (
              <span key={category} className="px-3 sm:px-5 py-1.5 sm:py-2.5 bg-slate-100 border border-slate-200 rounded-full text-xs sm:text-base font-medium text-slate-600">
                {category}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RevenueSection;
